import { useTheme } from '@mui/material';
import DataCard, {
  DataCardProps,
} from 'components/data-card/data-card.component';
import MovementIndicator, {
  Movement,
} from 'components/movement-indicator/movement-indicator.component';
import { SegmentGridVariant } from 'components/segment-grid/segment-grid.types';

export type SegmentCardProps = Omit<DataCardProps, 'valueType'> & {
  /**
   * Movement chip rendered in the description slot. Omit to render a plain card.
   */
  movement?: Movement;
  /**
   * Makes the movement chip clickable (e.g. to open the movement legend
   * drawer). Ignored when no movement is rendered.
   */
  onMovementClick?: () => void;
  /**
   * Visual variant — controls the card background color.
   * `default` = paper, `sliders` = warning.light, `churn` = error.light.
   */
  variant?: SegmentGridVariant;
  /**
   * SegmentCard accepts pre-formatted strings for `value` because each grid
   * formats counts/spend/visits differently. Pass the formatted string directly.
   */
  valueType?: DataCardProps['valueType'];
};

export const SegmentCard = ({
  movement,
  onMovementClick,
  variant = 'default',
  loading,
  slots,
  slotProps,
  valueType = 'string',
  'data-testid': dataTestId,
  ...rest
}: SegmentCardProps) => {
  const theme = useTheme();

  const backgroundColor = (() => {
    switch (variant) {
      case 'sliders':
        return theme.palette.mode === 'dark'
          ? '#1A1A1A'
          : theme.palette.warning.light;
      case 'churn':
        return theme.palette.mode === 'dark'
          ? '#1A1A1A'
          : theme.palette.error.light;
      case 'default':
      default:
        return theme.palette.mode === 'dark' ? '#1A1A1A' : '#DFE7FC';
    }
  })();

  return (
    <DataCard
      {...rest}
      data-testid={dataTestId}
      valueType={valueType}
      slots={{
        ...slots,
        description: movement ? (
          <MovementIndicator
            movement={movement}
            size="small"
            onClick={onMovementClick}
            data-testid={
              dataTestId ? `${dataTestId}.Movement` : 'SegmentCard.Movement'
            }
          />
        ) : (
          slots?.description
        ),
      }}
      slotProps={{
        ...slotProps,
        card: {
          ...slotProps?.card,
          style: {
            backgroundColor,
            // Loading dims the card while keeping prior data visible — matches
            // the in-flight refresh UX of the existing segment grid. Skeletons
            // (DataCard's default loading treatment) replace data and would
            // make every refresh feel like a cold load.
            opacity: loading ? 0.5 : 1,
            ...slotProps?.card?.style,
          },
          elevation: 0,
        },
        title: {
          variant: 'h3',
          ...slotProps?.title,
        },
        value: {
          variant: 'h2',
          component: 'div',
          ...slotProps?.value,
        },
      }}
    />
  );
};

export default SegmentCard;
